import type { TechDef } from './types';

// ─── Tier 1 Tech Tree ───────────────────────────────────────────────────────────
// Four branches × 5 slots. Slot 5 is always a flat unlock (maxLevel: 1).

export const TIER1_TECH: TechDef[] = [
  // ── Production Branch ────────────────────────────────────────────────────────
  {
    id: 't1_p1', tier: 1, branch: 'production', slot: 1,
    name: 'Optimized Harvesting',
    description: '+10% Tech Drone output per level',
    baseCost: 1e2, costGrowth: 2.0, maxLevel: 20,
    effect: { type: 'unit_output_mult', value: 0.10, targetUnitId: 1 },
  },
  {
    id: 't1_p2', tier: 1, branch: 'production', slot: 2,
    name: 'Industrial Efficiency',
    description: '+5% all Matter production per level',
    baseCost: 1e3, costGrowth: 2.2, maxLevel: 15,
    effect: { type: 'all_production_mult', value: 0.05 },
    unlockRequires: { type: 'unit_owned', unitId: 3, amount: 1 },
  },
  {
    id: 't1_p3', tier: 1, branch: 'production', slot: 3,
    name: 'Foundry Overhaul',
    description: '+15% production for Units 1-5 per level',
    baseCost: 1e5, costGrowth: 2.4, maxLevel: 10,
    effect: { type: 'chain_mult', value: 0.15, chainMaxUnit: 5 },
    unlockRequires: { type: 'unit_owned', unitId: 5, amount: 1 },
  },
  {
    id: 't1_p4', tier: 1, branch: 'production', slot: 4,
    name: 'Planetary Infrastructure',
    description: '+25% total Matter production per level',
    baseCost: 1e7, costGrowth: 2.6, maxLevel: 8,
    effect: { type: 'all_production_mult', value: 0.25 },
    unlockRequires: { type: 'tech_level', techId: 't1_p3', techLevel: 3 },
  },
  {
    id: 't1_p5', tier: 1, branch: 'production', slot: 5,
    name: 'Void Harvesting Protocol',
    description: 'Bonus = 1 + log10(totalMatter) × 0.05 (permanent)',
    baseCost: 1e11, costGrowth: 1, maxLevel: 1,
    effect: { type: 'log_scaling', value: 0.05 },
    unlockRequires: { type: 'tech_level', techId: 't1_p4', techLevel: 5 },
  },

  // ── Efficiency Branch ────────────────────────────────────────────────────────
  {
    id: 't1_e1', tier: 1, branch: 'efficiency', slot: 1,
    name: 'Micro-Fabrication',
    description: '-2% unit cost per level (max -50%)',
    baseCost: 1e2, costGrowth: 2.0, maxLevel: 25,
    effect: { type: 'cost_reduction', value: 0.02 },
  },
  {
    id: 't1_e2', tier: 1, branch: 'efficiency', slot: 2,
    name: 'Bulk Fabrication',
    description: '+10% value when bulk-buying per level',
    baseCost: 5e3, costGrowth: 2.2, maxLevel: 10,
    effect: { type: 'bulk_efficiency', value: 0.10 },
    unlockRequires: { type: 'unit_owned', unitId: 4, amount: 5 },
  },
  {
    id: 't1_e3', tier: 1, branch: 'efficiency', slot: 3,
    name: 'Salvage Protocols',
    description: '5% cost refund on every purchase per level',
    baseCost: 1e5, costGrowth: 2.4, maxLevel: 5,
    effect: { type: 'purchase_refund', value: 0.05 },
    unlockRequires: { type: 'tech_level', techId: 't1_e2', techLevel: 3 },
  },
  {
    id: 't1_e4', tier: 1, branch: 'efficiency', slot: 4,
    name: 'Precision Calibration',
    description: '-0.01 cost scaling per level (compresses exponent growth)',
    baseCost: 1e7, costGrowth: 2.6, maxLevel: 10,
    effect: { type: 'cost_scale_reduction', value: 0.01 },
    unlockRequires: { type: 'tech_level', techId: 't1_e3', techLevel: 2 },
  },
  {
    id: 't1_e5', tier: 1, branch: 'efficiency', slot: 5,
    name: 'Zero-Point Compression',
    description: 'All costs raised to power 0.95 (permanent)',
    baseCost: 1e11, costGrowth: 1, maxLevel: 1,
    effect: { type: 'all_cost_pow', value: 0.95 },
    unlockRequires: { type: 'tech_level', techId: 't1_e4', techLevel: 5 },
  },

  // ── Automation Branch ────────────────────────────────────────────────────────
  {
    id: 't1_a1', tier: 1, branch: 'automation', slot: 1,
    name: 'Drone AI',
    description: 'Enables auto-buy for Unit 1',
    baseCost: 1e3, costGrowth: 1, maxLevel: 1,
    effect: { type: 'autobuy', value: 1 },
    unlockRequires: { type: 'unit_owned', unitId: 2, amount: 1 },
  },
  {
    id: 't1_a2', tier: 1, branch: 'automation', slot: 2,
    name: 'Cluster Management',
    description: 'Enables auto-buy for Units 1-3',
    baseCost: 1e5, costGrowth: 1, maxLevel: 1,
    effect: { type: 'autobuy', value: 3 },
    unlockRequires: { type: 'tech_level', techId: 't1_a1', techLevel: 1 },
  },
  {
    id: 't1_a3', tier: 1, branch: 'automation', slot: 3,
    name: 'Production Control',
    description: 'Enables auto-buy for Units 1-10',
    baseCost: 1e7, costGrowth: 1, maxLevel: 1,
    effect: { type: 'autobuy', value: 10 },
    unlockRequires: { type: 'tech_level', techId: 't1_a2', techLevel: 1 },
  },
  {
    id: 't1_a4', tier: 1, branch: 'automation', slot: 4,
    name: 'Tactical Allocation',
    description: 'Smart auto-buy prioritizes best ROI units',
    baseCost: 1e9, costGrowth: 1, maxLevel: 1,
    effect: { type: 'smart_allocation', value: 1 },
    unlockRequires: { type: 'tech_level', techId: 't1_a3', techLevel: 1 },
  },
  {
    id: 't1_a5', tier: 1, branch: 'automation', slot: 5,
    name: 'Full Autonomy',
    description: 'All 25 units auto-buy + full idle mode',
    baseCost: 1e11, costGrowth: 1, maxLevel: 1,
    effect: { type: 'full_automation', value: 1 },
    unlockRequires: { type: 'tech_level', techId: 't1_a4', techLevel: 1 },
  },

  // ── Warfare Branch ───────────────────────────────────────────────────────────
  {
    id: 't1_w1', tier: 1, branch: 'warfare', slot: 1,
    name: 'Battle Formation Protocols',
    description: '+10% attack power per level',
    baseCost: 1e3, costGrowth: 2.0, maxLevel: 20,
    effect: { type: 'attack_mult', value: 0.10 },
    unlockRequires: { type: 'unit_owned', unitId: 23, amount: 1 },
  },
  {
    id: 't1_w2', tier: 1, branch: 'warfare', slot: 2,
    name: 'Fortification Grids',
    description: '+10% defense power per level',
    baseCost: 1e4, costGrowth: 2.0, maxLevel: 20,
    effect: { type: 'defense_mult', value: 0.10 },
    unlockRequires: { type: 'unit_owned', unitId: 24, amount: 1 },
  },
  {
    id: 't1_w3', tier: 1, branch: 'warfare', slot: 3,
    name: 'Raid Protocols',
    description: '+5% raid gain per level',
    baseCost: 1e6, costGrowth: 2.3, maxLevel: 10,
    effect: { type: 'raid_gain_mult', value: 0.05 },
    unlockRequires: { type: 'pvp_wins', amount: 1 },
  },
  {
    id: 't1_w4', tier: 1, branch: 'warfare', slot: 4,
    name: 'Wartime Economy',
    description: '+5% global production per level',
    baseCost: 1e8, costGrowth: 2.6, maxLevel: 8,
    effect: { type: 'all_production_mult', value: 0.05 },
    unlockRequires: { type: 'tech_level', techId: 't1_w3', techLevel: 3 },
  },
  {
    id: 't1_w5', tier: 1, branch: 'warfare', slot: 5,
    name: 'Total War Footing',
    description: '+15% production while any raid is active (permanent)',
    baseCost: 1e10, costGrowth: 1, maxLevel: 1,
    effect: { type: 'wartime_prod_mult', value: 0.15 },
    unlockRequires: { type: 'tech_level', techId: 't1_w4', techLevel: 3 },
  },
];

// ─── Tier 2 Tech Tree ───────────────────────────────────────────────────────────
// Same 4-branch structure, scaled up costs, stellar theming

export const TIER2_TECH: TechDef[] = [
  // ── Production Branch ────────────────────────────────────────────────────────
  {
    id: 't2_p1', tier: 2, branch: 'production', slot: 1,
    name: 'Optimized Extraction',
    description: '+10% Mining Drone output per level',
    baseCost: 1e5, costGrowth: 2.0, maxLevel: 20,
    effect: { type: 'unit_output_mult', value: 0.10, targetUnitId: 1 },
  },
  {
    id: 't2_p2', tier: 2, branch: 'production', slot: 2,
    name: 'Orbital Logistics',
    description: '+5% all Alloy production per level',
    baseCost: 1e6, costGrowth: 2.2, maxLevel: 15,
    effect: { type: 'all_production_mult', value: 0.05 },
    unlockRequires: { type: 'unit_owned', unitId: 3, amount: 1 },
  },
  {
    id: 't2_p3', tier: 2, branch: 'production', slot: 3,
    name: 'Stellar Engineering',
    description: '+15% production for Units 1-5 per level',
    baseCost: 1e8, costGrowth: 2.4, maxLevel: 10,
    effect: { type: 'chain_mult', value: 0.15, chainMaxUnit: 5 },
    unlockRequires: { type: 'unit_owned', unitId: 5, amount: 1 },
  },
  {
    id: 't2_p4', tier: 2, branch: 'production', slot: 4,
    name: 'System Mastery',
    description: '+25% total Alloy production per level',
    baseCost: 1e10, costGrowth: 2.6, maxLevel: 8,
    effect: { type: 'all_production_mult', value: 0.25 },
    unlockRequires: { type: 'tech_level', techId: 't2_p3', techLevel: 3 },
  },
  {
    id: 't2_p5', tier: 2, branch: 'production', slot: 5,
    name: 'Void Smelting Protocol',
    description: 'Bonus = 1 + log10(totalAlloys) × 0.05 (permanent)',
    baseCost: 1e14, costGrowth: 1, maxLevel: 1,
    effect: { type: 'log_scaling', value: 0.05 },
    unlockRequires: { type: 'tech_level', techId: 't2_p4', techLevel: 5 },
  },

  // ── Efficiency Branch ────────────────────────────────────────────────────────
  {
    id: 't2_e1', tier: 2, branch: 'efficiency', slot: 1,
    name: 'Nano-Fabrication',
    description: '-2% unit cost per level (max -50%)',
    baseCost: 1e5, costGrowth: 2.0, maxLevel: 25,
    effect: { type: 'cost_reduction', value: 0.02 },
  },
  {
    id: 't2_e2', tier: 2, branch: 'efficiency', slot: 2,
    name: 'Mass Production Lines',
    description: '+10% value when bulk-buying per level',
    baseCost: 5e6, costGrowth: 2.2, maxLevel: 10,
    effect: { type: 'bulk_efficiency', value: 0.10 },
    unlockRequires: { type: 'unit_owned', unitId: 4, amount: 5 },
  },
  {
    id: 't2_e3', tier: 2, branch: 'efficiency', slot: 3,
    name: 'Salvage Operations',
    description: '5% cost refund on every purchase per level',
    baseCost: 1e8, costGrowth: 2.4, maxLevel: 5,
    effect: { type: 'purchase_refund', value: 0.05 },
    unlockRequires: { type: 'tech_level', techId: 't2_e2', techLevel: 3 },
  },
  {
    id: 't2_e4', tier: 2, branch: 'efficiency', slot: 4,
    name: 'Deep Calibration',
    description: '-0.01 cost scaling per level',
    baseCost: 1e10, costGrowth: 2.6, maxLevel: 10,
    effect: { type: 'cost_scale_reduction', value: 0.01 },
    unlockRequires: { type: 'tech_level', techId: 't2_e3', techLevel: 2 },
  },
  {
    id: 't2_e5', tier: 2, branch: 'efficiency', slot: 5,
    name: 'Quantum Compression',
    description: 'All costs raised to power 0.95 (permanent)',
    baseCost: 1e14, costGrowth: 1, maxLevel: 1,
    effect: { type: 'all_cost_pow', value: 0.95 },
    unlockRequires: { type: 'tech_level', techId: 't2_e4', techLevel: 5 },
  },

  // ── Automation Branch ────────────────────────────────────────────────────────
  {
    id: 't2_a1', tier: 2, branch: 'automation', slot: 1,
    name: 'Drone Fleet AI',
    description: 'Enables auto-buy for Unit 1',
    baseCost: 1e6, costGrowth: 1, maxLevel: 1,
    effect: { type: 'autobuy', value: 1 },
    unlockRequires: { type: 'unit_owned', unitId: 2, amount: 1 },
  },
  {
    id: 't2_a2', tier: 2, branch: 'automation', slot: 2,
    name: 'Orbital Coordination',
    description: 'Enables auto-buy for Units 1-3',
    baseCost: 1e8, costGrowth: 1, maxLevel: 1,
    effect: { type: 'autobuy', value: 3 },
    unlockRequires: { type: 'tech_level', techId: 't2_a1', techLevel: 1 },
  },
  {
    id: 't2_a3', tier: 2, branch: 'automation', slot: 3,
    name: 'Fleet Control Matrix',
    description: 'Enables auto-buy for Units 1-10',
    baseCost: 1e10, costGrowth: 1, maxLevel: 1,
    effect: { type: 'autobuy', value: 10 },
    unlockRequires: { type: 'tech_level', techId: 't2_a2', techLevel: 1 },
  },
  {
    id: 't2_a4', tier: 2, branch: 'automation', slot: 4,
    name: 'Strategic Allocation AI',
    description: 'Smart auto-buy prioritizes best ROI units',
    baseCost: 1e12, costGrowth: 1, maxLevel: 1,
    effect: { type: 'smart_allocation', value: 1 },
    unlockRequires: { type: 'tech_level', techId: 't2_a3', techLevel: 1 },
  },
  {
    id: 't2_a5', tier: 2, branch: 'automation', slot: 5,
    name: 'System Autonomy Protocol',
    description: 'All 25 units auto-buy + full idle mode',
    baseCost: 1e14, costGrowth: 1, maxLevel: 1,
    effect: { type: 'full_automation', value: 1 },
    unlockRequires: { type: 'tech_level', techId: 't2_a4', techLevel: 1 },
  },

  // ── Warfare Branch ───────────────────────────────────────────────────────────
  {
    id: 't2_w1', tier: 2, branch: 'warfare', slot: 1,
    name: 'Fleet Battle Formations',
    description: '+10% attack power per level',
    baseCost: 1e6, costGrowth: 2.0, maxLevel: 20,
    effect: { type: 'attack_mult', value: 0.10 },
    unlockRequires: { type: 'unit_owned', unitId: 23, amount: 1 },
  },
  {
    id: 't2_w2', tier: 2, branch: 'warfare', slot: 2,
    name: 'Defense Grid Arrays',
    description: '+10% defense power per level',
    baseCost: 1e7, costGrowth: 2.0, maxLevel: 20,
    effect: { type: 'defense_mult', value: 0.10 },
    unlockRequires: { type: 'unit_owned', unitId: 24, amount: 1 },
  },
  {
    id: 't2_w3', tier: 2, branch: 'warfare', slot: 3,
    name: 'Deep Strike Protocols',
    description: '+5% raid gain per level',
    baseCost: 1e9, costGrowth: 2.3, maxLevel: 10,
    effect: { type: 'raid_gain_mult', value: 0.05 },
    unlockRequires: { type: 'pvp_wins', amount: 3 },
  },
  {
    id: 't2_w4', tier: 2, branch: 'warfare', slot: 4,
    name: 'Wartime Economy',
    description: '+5% global production per level',
    baseCost: 1e11, costGrowth: 2.6, maxLevel: 8,
    effect: { type: 'all_production_mult', value: 0.05 },
    unlockRequires: { type: 'tech_level', techId: 't2_w3', techLevel: 3 },
  },
  {
    id: 't2_w5', tier: 2, branch: 'warfare', slot: 5,
    name: 'Total Fleet Mobilization',
    description: '+15% production while any raid is active (permanent)',
    baseCost: 1e13, costGrowth: 1, maxLevel: 1,
    effect: { type: 'wartime_prod_mult', value: 0.15 },
    unlockRequires: { type: 'tech_level', techId: 't2_w4', techLevel: 3 },
  },
];

// ─── Lookup helpers ─────────────────────────────────────────────────────────────

const TECH_MAP: Record<number, TechDef[]> = {
  1: TIER1_TECH,
  2: TIER2_TECH,
};

export function getTechForTier(tier: number): TechDef[] {
  return TECH_MAP[tier] ?? [];
}

export function getTechDef(techId: string): TechDef | undefined {
  for (const defs of Object.values(TECH_MAP)) {
    const found = defs.find((t) => t.id === techId);
    if (found) return found;
  }
  return undefined;
}

export function getAllTech(): TechDef[] {
  return Object.values(TECH_MAP).flat();
}
